/**
 * Confirm SKUs
 *
 * Shown before a PO with look-alike items is saved (and offered from the
 * editor). One row per unconfirmed line: the item the line points at, plus
 * every item that could be mistaken for it, each with the reason. The person
 * picks the right one; the choice is stored on the line so it is not asked
 * again unless the item changes. A line whose item is on the linked ShipBob
 * WRO never gets here — ShipBob is the truth for those.
 */

import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  Box,
} from "@mui/material";
import { POLineItem } from "models/InventoryModel";
import { InventoryOption, LookAlike } from "shared/utils/skuAmbiguity";

export type SkuQuestion = {
  /** Index into the editor's line list */
  index: number;
  line: POLineItem;
  current: InventoryOption;
  lookAlikes: LookAlike[];
};

interface ConfirmSkusDialogProps {
  open: boolean;
  questions: SkuQuestion[];
  /** Set when the dialog was opened by Save: the save continues after confirming */
  continueLabel?: string;
  onCancel: () => void;
  /** Chosen inventory id per line index */
  onConfirm: (choices: Record<number, string>) => void;
}

export default function ConfirmSkusDialog({ open, questions, continueLabel, onCancel, onConfirm }: ConfirmSkusDialogProps): JSX.Element {
  const [choices, setChoices] = useState<Record<number, string>>({});

  useEffect(() => {
    if (!open) return;
    const initial: Record<number, string> = {};
    for (const q of questions) initial[q.index] = q.current.id;
    setChoices(initial);
  }, [open, questions]);

  const label = (opt: InventoryOption) => `${opt.id} - ${opt.name || "(no name)"}${opt.skus.length ? ` · ${opt.skus[0]}` : ""}`;

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle>Confirm the ShipBob items on this PO</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {questions.length === 1 ? "One line points" : `${questions.length} lines point`} at an item that has look-alikes in ShipBob —
          an older listing, the same product from another factory, or another item sharing the Danican product number.
          Pick the item this order actually restocks. Stock levels are deliberately not used to guess.
        </Alert>
        {questions.map(q => (
          <Box key={q.index} sx={{ mb: 2.5 }}>
            <Typography variant="subtitle2">
              Line {q.index + 1} · {q.line.quantity.toLocaleString()} × {q.current.name || q.current.id}
              {q.line.manufacturerProductNumber ? ` · Danican # ${q.line.manufacturerProductNumber}` : ""}
            </Typography>
            <RadioGroup
              value={choices[q.index] ?? q.current.id}
              onChange={(e) => setChoices(prev => ({ ...prev, [q.index]: e.target.value }))}
            >
              <FormControlLabel value={q.current.id} control={<Radio size="small" />} label={`${label(q.current)} (as entered)`} />
              {q.lookAlikes.map(alt => (
                <FormControlLabel
                  key={alt.option.id}
                  value={alt.option.id}
                  control={<Radio size="small" />}
                  label={
                    <span>
                      {label(alt.option)}
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>{alt.reasons.join(", ")}</Typography>
                    </span>
                  }
                />
              ))}
            </RadioGroup>
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" onClick={() => onConfirm(choices)}>{continueLabel || "Confirm"}</Button>
      </DialogActions>
    </Dialog>
  );
}
